import { Popover, PopoverContent, PopoverTrigger } from "./popover";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { userService } from "@/services/api/user";
import { useEffect, useState } from "react";

import { ClientProvider } from "@/interfaces";
import { cn, errorHandler } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "./button";
import { FormControl } from "./form";

interface ClientProviderSelectProps {
  value?: number;
  onChange: (value: number | null) => void;
  userId?: number;
  addEmptyOption?: boolean;
  providerType?: number;
}

const ClientProviderSelect = ({
  value,
  onChange,
  userId,
  addEmptyOption = true,
  providerType = 2,
}: ClientProviderSelectProps) => {
  const [providers, setProviders] = useState<ClientProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const loadProviders = async () => {
      try {
        setLoading(true);

        const response = await userService.getClientProviders(userId ?? 0);
        const data = response.data;

        const filteredProviders = data.filter(
          (provider) => provider.type === providerType,
        );
        setProviders(filteredProviders);
      } catch (err) {
        errorHandler(err, "Erro ao carregar fornecedores.");
      } finally {
        setLoading(false);
      }
    };

    loadProviders();
  }, [userId, providerType]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={loading}
            className={cn(
              "justify-between dark:border-white",
              !value && "text-muted-foreground",
            )}
          >
            {loading
              ? "Carregando fornecedores..."
              : value
                ? providers.find((provider) => provider.id === value)?.name
                : addEmptyOption
                  ? "Todos fornecedores"
                  : "Selecione um fornecedor"}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput
            placeholder="Procure um fornecedor..."
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>Nenhum fornecedor encontrado.</CommandEmpty>
            <CommandGroup>
              {addEmptyOption && (
                <>
                  <CommandItem
                    value="Todos fornecedores"
                    onSelect={() => {
                      setOpen(false);
                      onChange(null);
                    }}
                  >
                    Todos fornecedores
                    <Check
                      className={cn(
                        "ml-auto",
                        value === null ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                  <CommandItem
                    value="Outro"
                    onSelect={() => {
                      setOpen(false);
                      onChange(null);
                    }}
                  >
                    Outro
                    <Check
                      className={cn(
                        "ml-auto",
                        value === null ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                </>
              )}
              {providers.length ? (
                providers.map((provider) => (
                  <CommandItem
                    value={provider.name}
                    key={provider.id}
                    onSelect={() => {
                      setOpen(false);
                      onChange(provider.id);
                    }}
                  >
                    {provider.name}
                    <Check
                      className={cn(
                        "ml-auto",
                        provider.id === value ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))
              ) : (
                <CommandItem disabled>Nenhum fornecedor encontrado</CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ClientProviderSelect;
